import {useCallback, useRef, useState} from 'react';
import {addReference} from '../../../../../components/prosemirror-editor/yMaps/referencesYMap';
import {
    addFolder as yAddFolder,
    addItem as yAddItem,
    getAllItems,
} from '../../../../../components/prosemirror-editor/yMaps/sourcePackageYMap';
import {createSourcePackageItem} from '../../../../../utils/api/sourcePackage';
import {useSession} from '../../../../../firebase/UserProvider';
import useYDocStore from '../../../../../stores/useYDocStore';
import {
    buildSourceFileSelectionSnippet,
    computeBlockSelection,
    getBlockElementFromNode,
} from './sourceFileSelectionUtils';

/**
 * Context-menu cite / source-package actions for uploaded source files opened
 * from a FINTEL draft. Mirrors useDocumentSelectionMenu but resolves selections
 * against [data-block-index] blocks instead of corpus #segment-{n} nodes.
 */
export default function useSourceFileSelectionMenu({
    reportId,
    referenceId,
    docTitle,
    docUrl = null,
    segmentsByBlockIndex = null,
    toCanonicalBlockIndex = null,
    contentRef,
}) {
    const {user} = useSession();
    const ydoc = useYDocStore((state) => state.ydoc);
    const yReferenceMarks = useYDocStore((state) => state.yReferenceMarks);

    const [menuPosition, setMenuPosition] = useState(null);
    const [citeAnchorEl, setCiteAnchorEl] = useState(null);
    const selectedRangeRef = useRef(null);

    const handleMenuClose = useCallback(() => {
        setMenuPosition(null);
        setCiteAnchorEl(null);
    }, []);

    const handleContextMenu = useCallback((e) => {
        if (!reportId || !referenceId) return;
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;

        const range = sel.getRangeAt(0).cloneRange();
        const containerNode = range.commonAncestorContainer.nodeType === 3
            ? range.commonAncestorContainer.parentNode
            : range.commonAncestorContainer;
        if (!contentRef.current || !contentRef.current.contains(containerNode)) return;

        const startBlock = getBlockElementFromNode(contentRef.current, range.startContainer);
        const endBlock = getBlockElementFromNode(contentRef.current, range.endContainer);
        if (!startBlock || !endBlock) return;

        e.preventDefault();
        selectedRangeRef.current = range;
        setMenuPosition({mouseX: e.clientX + 2, mouseY: e.clientY - 6});
    }, [contentRef, referenceId, reportId]);

    const handleMouseDown = useCallback((e) => {
        if (!e.ctrlKey) return;
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
        const range = sel.getRangeAt(0);
        const containerNode = range.commonAncestorContainer;
        if (!contentRef.current || !contentRef.current.contains(containerNode)) return;
        e.preventDefault();
    }, [contentRef]);

    const buildSelectionSnippet = useCallback((range) => (
        buildSourceFileSelectionSnippet(range, contentRef.current, {
            referenceId,
            docTitle,
            docUrl,
            segmentsByBlockIndex,
            toCanonicalBlockIndex,
        })
    ), [contentRef, docTitle, docUrl, referenceId, segmentsByBlockIndex, toCanonicalBlockIndex]);

    const handleCiteToFragment = useCallback(async (fragmentUid) => {
        try {
            const range = selectedRangeRef.current;
            if (!range || !reportId || !fragmentUid) return handleMenuClose();

            const selectedText = range.toString();
            if (!selectedText?.trim()) return handleMenuClose();

            const norm = computeBlockSelection(range, contentRef.current);
            if (!norm) return handleMenuClose();

            let {startIndex, startOffset, endIndex, endOffset} = norm;
            if (typeof toCanonicalBlockIndex === 'function') {
                startIndex = toCanonicalBlockIndex(startIndex) ?? startIndex;
                endIndex = toCanonicalBlockIndex(endIndex) ?? endIndex;
            }
            const startSegId = segmentsByBlockIndex?.[startIndex] ?? startIndex;
            const localId = [referenceId, startIndex, startOffset, endIndex, endOffset].join(':');

            const referenceData = {
                localId,
                docId: referenceId,
                docUrl,
                docTitle,
                content: selectedText,
                segmentId: startSegId,
                index: startIndex,
                offset: startOffset,
                start: {segmentIndex: startIndex, segmentOffset: startOffset},
                end: {segmentIndex: endIndex, segmentOffset: endOffset},
            };

            if (ydoc && yReferenceMarks) {
                addReference(ydoc, yReferenceMarks, fragmentUid, referenceData);
            }

            try {
                const snippet = buildSelectionSnippet(range);
                if (snippet?.localId) {
                    const {ydoc: yDoc, ySourcePackage} = useYDocStore.getState();
                    if (yDoc && ySourcePackage) {
                        const existsAnywhere = getAllItems(ySourcePackage)
                            .some((item) => item?.localId === snippet.localId);
                        if (!existsAnywhere) {
                            yAddItem(yDoc, ySourcePackage, {
                                type: 'snippet',
                                parentFolderId: null,
                                ...snippet,
                            });
                        }
                    }
                }
            } catch {
                // ignore add-to-package errors
            }
        } catch (error) {
            console.error('[useSourceFileSelectionMenu] Failed to add reference:', error);
        } finally {
            window.getSelection()?.removeAllRanges();
            selectedRangeRef.current = null;
            handleMenuClose();
        }
    }, [
        buildSelectionSnippet,
        contentRef,
        docTitle,
        docUrl,
        handleMenuClose,
        referenceId,
        reportId,
        segmentsByBlockIndex,
        toCanonicalBlockIndex,
        yReferenceMarks,
        ydoc,
    ]);

    const handleDragStartSelection = useCallback((e) => {
        try {
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
            const range = sel.getRangeAt(0);
            if (!contentRef.current?.contains(range.commonAncestorContainer)) return;
            const snippet = buildSelectionSnippet(range);
            if (!snippet) return;
            e.dataTransfer?.setData?.('application/x-snippet', JSON.stringify(snippet));
            e.dataTransfer.effectAllowed = 'copy';
        } catch {
            // ignore
        }
    }, [buildSelectionSnippet, contentRef]);

    const addSelectionToFolder = useCallback((folderId = null) => {
        try {
            const range = selectedRangeRef.current;
            if (!range) return handleMenuClose();
            const snippet = buildSelectionSnippet(range);
            if (!snippet) return handleMenuClose();
            const {ydoc: yDoc, ySourcePackage} = useYDocStore.getState();
            if (yDoc && ySourcePackage) {
                yAddItem(yDoc, ySourcePackage, {
                    type: 'snippet',
                    parentFolderId: folderId ?? null,
                    ...snippet,
                });
            }
        } finally {
            window.getSelection()?.removeAllRanges();
            selectedRangeRef.current = null;
            handleMenuClose();
        }
    }, [buildSelectionSnippet, handleMenuClose]);

    const onAddToSourceRoot = useCallback(() => addSelectionToFolder(null), [addSelectionToFolder]);
    const onAddToSourceFolder = useCallback((folderId) => addSelectionToFolder(folderId), [addSelectionToFolder]);

    const onAddToSourceNewFolder = useCallback(() => {
        try {
            const range = selectedRangeRef.current;
            if (!range) return handleMenuClose();
            const snippet = buildSelectionSnippet(range);
            if (!snippet) return handleMenuClose();
            const {ydoc: yDoc, ySourcePackage} = useYDocStore.getState();
            if (!yDoc || !ySourcePackage) return handleMenuClose();
            const newFolderId = yAddFolder(yDoc, ySourcePackage, {
                name: 'New folder',
                parentFolderId: null,
            });
            yAddItem(yDoc, ySourcePackage, {
                type: 'snippet',
                parentFolderId: newFolderId,
                ...snippet,
            });
        } catch {
            // ignore
        } finally {
            window.getSelection()?.removeAllRanges();
            selectedRangeRef.current = null;
            handleMenuClose();
        }
    }, [buildSelectionSnippet, handleMenuClose]);

    const onAddToAnotherProduct = useCallback(async (productId, folderId = null) => {
        try {
            const range = selectedRangeRef.current;
            if (!range || !productId) return handleMenuClose();
            const snippet = buildSelectionSnippet(range);
            if (!snippet) return handleMenuClose();

            await createSourcePackageItem(productId, {
                type: 'DOCUMENT_FRAGMENT',
                parentFolderId: folderId,
                entity: {
                    docId: snippet.docId,
                    start: snippet.start,
                    end: snippet.end,
                    content: snippet.content,
                    docTitle: snippet.docTitle,
                    docUrl: snippet.docUrl,
                    segmentId: snippet.segmentId,
                },
                userId: user?.uid,
            });
        } catch (error) {
            console.error('[useSourceFileSelectionMenu] Failed to add to another product:', error);
        } finally {
            window.getSelection()?.removeAllRanges();
            selectedRangeRef.current = null;
            handleMenuClose();
        }
    }, [buildSelectionSnippet, handleMenuClose, user?.uid]);

    return {
        menuPosition,
        citeAnchorEl,
        setCiteAnchorEl,
        handleContextMenu,
        handleMouseDown,
        handleMenuClose,
        handleCiteToFragment,
        handleDragStartSelection,
        onAddToSourceRoot,
        onAddToSourceFolder,
        onAddToSourceNewFolder,
        onAddToAnotherProduct,
    };
}
